import type {
  ListResponsePriceOverride,
  OrderStatus,
  PriceOverrideSort,
  PriceOverrideStatus,
  SortOrder,
} from "@classes-service-types/index.ts";
import type { UseQueryOptions } from "@tanstack/vue-query";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { format } from "date-fns";
import { computed, type Ref, unref } from "vue";
import { PRICE_OVERRIDE_API_DATE_FORMAT } from "@/constants.ts";
import { learningServiceRequest } from "@/services/utils";

export const QUERY_KEY = "getPriceOverrides";

export const useGetPriceOverrides = <TData = ListResponsePriceOverride>(
  status?: Ref<Array<PriceOverrideStatus> | undefined>,
  session?: Ref<Array<string>>,
  city?: Ref<Array<number>>,
  libraryId?: Ref<Array<string>>,
  overrideReasonId?: Ref<Array<string>>,
  orderStatus?: Ref<Array<string>>,
  decisionDateStart?: Ref<Date | string | undefined>,
  decisionDateEnd?: Ref<Date | string | undefined>,
  q?: Ref<string | undefined>,
  limit?: Ref<number> | number,
  skip?: Ref<number> | number,
  field?: Ref<PriceOverrideSort> | PriceOverrideSort,
  order?: Ref<SortOrder> | SortOrder,
  getArchived?: Ref<boolean> | boolean,
  useQueryOptions?: Partial<UseQueryOptions<ListResponsePriceOverride, Error, TData>>,
) => {
  const fetchPriceOverridesCount = (): Promise<ListResponsePriceOverride> => {
    return learningServiceRequest({
      method: "get",
      url: "/price_overrides/",
      params: {
        status: unref(status),
        session: unref(session),
        city: unref(city),
        library_id: unref(libraryId),
        override_reason_id: unref(overrideReasonId),
        order_status: unref(orderStatus),
        decision_date_start:
          decisionDateStart?.value instanceof Date
            ? format(decisionDateStart?.value, PRICE_OVERRIDE_API_DATE_FORMAT)
            : unref(decisionDateStart),
        decision_date_end:
          decisionDateEnd?.value instanceof Date
            ? format(decisionDateEnd?.value, PRICE_OVERRIDE_API_DATE_FORMAT)
            : unref(decisionDateEnd),
        limit: unref(limit),
        skip: unref(skip),
        field: unref(field),
        order: unref(order),
        get_archived: unref(getArchived),
        q: unref(q),
      },
    }).then((response: AxiosResponse<ListResponsePriceOverride>) => response.data);
  };

  return useQuery<ListResponsePriceOverride, Error, TData>({
    queryKey: computed(() => [
      QUERY_KEY,
      status,
      session,
      city,
      libraryId,
      overrideReasonId,
      orderStatus,
      decisionDateStart,
      decisionDateEnd,
      limit,
      skip,
      field,
      order,
      getArchived,
      q,
    ]),
    queryFn: fetchPriceOverridesCount,
    ...useQueryOptions,
  });
};
